import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type { ApproveAPInvoiceInput, ApproveAPInvoiceResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

export interface UseApproveAPInvoiceOptions {
  onSuccess?: (
    data: ApproveAPInvoiceResponse,
    variables: ApproveAPInvoiceInput,
  ) => void;
}

/**
 * Hook for approving AP invoices.
 * Handles the API call, success/error states, and cache invalidation.
 */
export function useApproveAPInvoice(options?: UseApproveAPInvoiceOptions) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn(
    (orpc, { id }: { id: string }) => [
      orpc.accountsPayable.invoices.get.queryKey({ input: { id } }),
      orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
    ],
  );
  const { showToast } = useToast();

  return useMutation(
    orpc.accountsPayable.invoices.approve.mutationOptions({
      onSuccess: (data, variables) => {
        showToast({
          title: 'Success',
          message: 'Invoice approved successfully',
          color: 'success',
          icon: 'check',
        });

        invalidateRpcQueryFn({ id: variables.id });

        options?.onSuccess?.(data, variables);
      },
      onError: (error) => {
        showToast({
          title: 'Error',
          message: error.message || 'Failed to approve invoice',
          color: 'error',
          icon: 'x',
        });
      },
    }),
  );
}
